import { type FormEvent, type ReactNode, useRef, useState } from 'react';

import type { RouteComponentProps, RouteElement } from '@shared/router';
import Link from '@shared/router/link';
import * as history from '@shared/router/location';
import RouteNotFound from '@shared/views/bootstrap/RouteNotFound';

import { Badge } from '@playbook/atoms/badge';
import { Button } from '@playbook/atoms/button';
import { Card } from '@playbook/atoms/card';
import { ChoiceCard } from '@playbook/compositions/choice-card';
import { RadioGroup } from '@playbook/compositions/radio-group';

import { ApiError, wasRefused } from '@portal/pkg/api';
import { useKeyActions } from '@portal/pkg/api/keys';
import { Action, type Key } from '@portal/pkg/api/types';
import { generateKeypair } from '@portal/pkg/crypto/keypair';
import * as routes from '@portal/pkg/routes';
import { Notice } from '@portal/views/_shared/notice';
import { PageHeader } from '@portal/views/_shared/page';
import { ExpiryPicker, isExpiryValid } from '@portal/views/keys/ExpiryPicker';
import { KeyIssued } from '@portal/views/keys/KeyIssued';
import {
	KeyMaterialChoice,
	type KeyMaterialSource,
	looksLikePublicKey,
} from '@portal/views/keys/KeyMaterialChoice';
import { KeyPageFrame, useKey } from '@portal/views/keys/KeyPageFrame';
import { UnsavedPrivateKey } from '@portal/views/keys/UnsavedPrivateKey';
import { NotPermitted, can } from '@portal/views/keys/_shared';

import css from './styles.module.css';

export default function RotateKeyPage({
	params,
}: RouteComponentProps): RouteElement {
	return (
		<KeyPageFrame>
			<RotateKey id={params?.['key']?.toString()} />
		</KeyPageFrame>
	);
}

function RotateKey({ id }: { id: Optional<string> }): ReactNode {
	const key = useKey(id);
	const { rotateKey } = useKeyActions();

	const [source, setSource] = useState<KeyMaterialSource>('generate');
	const [pastedKey, setPastedKey] = useState('');
	const [expiryChoice, setExpiryChoice] = useState('0');
	const [expiresAt, setExpiresAt] = useState(0);
	const [revokeCurrent, setRevokeCurrent] = useState(false);

	const [issued, setIssued] = useState<Nullable<Key>>(null);
	const [error, setError] = useState<Nullable<string>>(null);
	const [submitting, setSubmitting] = useState(false);

	// Refs for the same reasons as on the create page: the private key must
	// not be a render input and must die with this component, and the public
	// half rides along so a retry sends the same pair rather than orphaning
	// the replacement the first attempt may already have made.
	const privateKey = useRef<Nullable<string>>(null);
	const generatedKey = useRef<Nullable<string>>(null);

	// Before anything that depends on the key: rotating replaces it, so the
	// key this page was opened on is a replaced one by the time the result
	// comes back. Checking permission first would throw away the one screen
	// the private key is ever shown on.
	if (issued) {
		return (
			<div className={css.page}>
				<PageHeader
					title={issued.name}
					description="Rotated just now. The old key is being wound down; deploy this one in its place."
				/>
				<KeyIssued issued={issued} privateKey={privateKey.current} />
			</div>
		);
	}

	if (!key) {
		return <RouteNotFound />;
	}

	if (!can(key, Action.RotateKey)) {
		return <NotPermitted what="rotate this key" />;
	}

	async function handleSubmit(event: FormEvent): Promise<void> {
		event.preventDefault();

		if (!key) {
			return;
		}

		setError(null);
		setSubmitting(true);

		try {
			let publicKey = pastedKey;

			if (source === 'generate') {
				if (!generatedKey.current || !privateKey.current) {
					const generated = await generateKeypair();

					generatedKey.current = generated.publicKey;
					privateKey.current = generated.privateKey;
				}

				publicKey = generatedKey.current;
			}

			setIssued(
				await rotateKey(key.id, { publicKey, expiresAt, revokeCurrent })
			);
		} catch (caught) {
			// Kept unless the server refused outright: a rotation whose answer
			// was lost leaves a replacement nobody holds the private half of,
			// and this is the only copy of it. A conflict is not that refusal —
			// the successor it reports may be this browser's own earlier
			// attempt, holding the public half of the pair still in memory.
			if (wasRefused(caught) && !isConflict(caught)) {
				privateKey.current = null;
				generatedKey.current = null;
			}

			setError(rotateError(caught));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className={css.page}>
			<PageHeader
				title="Rotate key"
				description="Makes a new key with the same name and the same access, and winds this one down."
				breadcrumb={
					<Link href={routes.Key(key.id)}>
						<span className={css.breadcrumb}>← {key.name}</span>
					</Link>
				}
			/>

			<Card>
				<form className={css.form} onSubmit={handleSubmit}>
					{error && (
						<Notice variant="error" title="The key was not rotated">
							{error}
						</Notice>
					)}

					{error && privateKey.current && (
						<UnsavedPrivateKey name={key.name} privateKey={privateKey.current} />
					)}

					<div className={css.carried}>
						<div>
							<span className={css.quiet}>Carried over, unchanged</span>
							<p className={css.name}>{key.name}</p>
						</div>
						<ul className={css.capabilities}>
							{key.capabilities.map((capability) => (
								<li key={capability}>
									<Badge tint="200" label={capability} />
								</li>
							))}
						</ul>
					</div>

					<KeyMaterialChoice
						source={source}
						publicKey={pastedKey}
						onSourceChange={setSource}
						onPublicKeyChange={setPastedKey}
					/>

					<ExpiryPicker
						expiresAt={expiresAt}
						choice={expiryChoice}
						onChange={(choice, chosen) => {
							setExpiryChoice(choice);
							setExpiresAt(chosen);
						}}
					/>

					<RadioGroup
						name="revokeCurrent"
						label="What happens to the current key">
						<ChoiceCard
							name="revokeCurrent"
							value="grace"
							checked={!revokeCurrent}
							label="Give it 24 hours"
							description="Both keys work while you deploy the new one. The ordinary, planned rotation."
							onChange={() => setRevokeCurrent(false)}
						/>
						<ChoiceCard
							name="revokeCurrent"
							value="now"
							checked={revokeCurrent}
							label="Kill it now"
							description="Anything still using the old key breaks immediately. For when the private key has leaked."
							onChange={() => setRevokeCurrent(true)}
						/>
					</RadioGroup>

					<p className={css.quiet}>
						A key can only be rotated once. If someone else has already rotated
						this one, you will be told rather than given a second successor.
					</p>

					<div className={css.actions}>
						<Button
							label="Cancel"
							onClick={() => history.push(routes.Key(key.id))}
						/>
						<Button
							variant="primary"
							type="submit"
							label="Rotate key"
							loading={submitting}
							disabled={
								submitting ||
								!isExpiryValid(expiryChoice, expiresAt) ||
								(source === 'paste' && !looksLikePublicKey(pastedKey))
							}
						/>
					</div>
				</form>
			</Card>
		</div>
	);
}

function isConflict(caught: unknown): boolean {
	return caught instanceof ApiError && caught.status === 409;
}

function rotateError(caught: unknown): string {
	if (!wasRefused(caught)) {
		return 'Could not reach the server, so the key may have been rotated. Check your keys before trying again.';
	}

	// Not "someone else has": a rotation whose answer was lost looks exactly
	// like this on the retry, and telling the customer to go looking for
	// another administrator sends them away from the replacement they made.
	if (isConflict(caught)) {
		return 'This key has already been rotated, which may have been your own attempt just now. Open your keys to see the replacement.';
	}

	return (caught as ApiError).message;
}
